import React, { useContext, useEffect, useState } from 'react'
import TableView from '../../component/common/tableView'
import { useDispatch, useSelector } from 'react-redux'
import { itemRender } from '../../helper/paginationfunction'
import { SpinnerContext } from '../../context/spinnerContext'
import { getAdminHostTasks, getAdminHostTaskDetail, getAdminHostTaskStats } from '../../redux/slices/hostTaskSlice'
import { Badge } from 'reactstrap'
import CustomModal from '../../component/customModal'
import { errorToast } from '../../util/Toast/toast'
import { convertTimestampToDate } from '../../helper/function'

const statusOptions = [
  { value: '', label: 'All Status' },
  { value: 'OPEN', label: 'Open' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'COMPLETED', label: 'Completed' }
]

const priorityOptions = [
  { value: '', label: 'All Priority' },
  { value: 'LOW', label: 'Low' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HIGH', label: 'High' }
]

const sourceOptions = [
  { value: '', label: 'All Sources' },
  { value: 'MANUAL', label: 'Manual' },
  { value: 'CLEANING_REPORT', label: 'Cleaning Report' }
]

const HostTasks = () => {
  const dispatch = useDispatch()
  const { changeLoader } = useContext(SpinnerContext)
  const { tasks, totalRecords, stats, taskDetail } = useSelector(({ hostTask }) => hostTask)
  const [pagesize, setPagesize] = useState(10)
  const [page, setPage] = useState(1)
  const [isRefresh, setIsRefresh] = useState(true)
  const [statusFilter, setStatusFilter] = useState('')
  const [priorityFilter, setPriorityFilter] = useState('')
  const [sourceFilter, setSourceFilter] = useState('')
  const [showDetailModal, setShowDetailModal] = useState(false)

  const toggleDetailModal = () => setShowDetailModal(!showDetailModal)

  useEffect(() => {
    dispatch(getAdminHostTaskStats())
  }, [])

  useEffect(() => {
    if (!isRefresh) {
      return
    }
    const params = {
      page,
      limit: pagesize
    }
    if (statusFilter) params.status = statusFilter
    if (priorityFilter) params.priority = priorityFilter
    if (sourceFilter) params.source = sourceFilter

    changeLoader(true)
    dispatch(getAdminHostTasks(params))
      .then(({ payload }) => {
        if (payload?.status === 200) {
          changeLoader(false)
          setIsRefresh(false)
        } else {
          errorToast(payload?.data?.status_message || 'Something went wrong!')
          changeLoader(false)
        }
      })
      .catch(() => {
        changeLoader(false)
        setIsRefresh(false)
      })
  }, [page, pagesize, isRefresh])

  const handlePagination = async (page, pageSize) => {
    setPage(page)
    setPagesize(pageSize)
    setIsRefresh(true)
  }

  const handleFilterChange = (setter) => (e) => {
    setter(e.target.value)
    setPage(1)
    setIsRefresh(true)
  }

  const handleRowClick = (record) => {
    const taskId = record?.host_task_id || record?.id
    if (taskId) {
      changeLoader(true)
      dispatch(getAdminHostTaskDetail(taskId))
        .then(({ payload }) => {
          changeLoader(false)
          if (payload?.status === 200) {
            setShowDetailModal(true)
          } else {
            errorToast(payload?.data?.status_message || 'Something went wrong!')
          }
        })
        .catch(() => {
          changeLoader(false)
        })
    }
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case 'OPEN':
        return <Badge color="warning">Open</Badge>
      case 'IN_PROGRESS':
        return <Badge color="info">In Progress</Badge>
      case 'COMPLETED':
        return <Badge color="success">Completed</Badge>
      default:
        return <Badge color="secondary">{status || '-'}</Badge>
    }
  }

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'LOW':
        return <Badge color="secondary">Low</Badge>
      case 'MEDIUM':
        return <Badge color="warning">Medium</Badge>
      case 'HIGH':
        return <Badge color="danger">High</Badge>
      default:
        return <Badge color="secondary">{priority || '-'}</Badge>
    }
  }

  const columns = [
    {
      title: 'No',
      render: (value, item, index) => (page - 1) * pagesize + (index + 1)
    },
    {
      title: 'Title',
      dataIndex: 'title',
      render: (text) => (text ? text : '-')
    },
    {
      title: 'Host Name',
      render: (record) => {
        const client = record?.client
        if (client) {
          return `${client.first_name || ''} ${client.last_name || ''}`.trim() || '-'
        }
        return '-'
      }
    },
    {
      title: 'Property',
      render: (record) => record?.property?.property_name || '-'
    },
    {
      title: 'Category',
      dataIndex: 'category',
      render: (text) => (text ? text : '-')
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      render: (text) => getPriorityBadge(text)
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (text) => getStatusBadge(text)
    },
    {
      title: 'Source',
      dataIndex: 'source',
      render: (text) => (text ? text.replace('_', ' ') : '-')
    },
    {
      title: 'Created Date',
      dataIndex: 'created_at',
      render: (text) => (text ? convertTimestampToDate(text) : '-')
    }
  ].filter(Boolean)

  return (
    <div className="page-wrapper customers">
      <div className="content container-fluid">
        <div className="page-header">
          <div className="content-page-header">
            <h5>Host Tasks</h5>
          </div>
        </div>

        {/* Stats Section */}
        <div className="row mb-4">
          <div className="col-md-4">
            <div className="card">
              <div className="card-body text-center">
                <h6 className="text-warning mb-1">Open</h6>
                <h3>{stats?.OPEN || 0}</h3>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card">
              <div className="card-body text-center">
                <h6 className="text-info mb-1">In Progress</h6>
                <h3>{stats?.IN_PROGRESS || 0}</h3>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card">
              <div className="card-body text-center">
                <h6 className="text-success mb-1">Completed</h6>
                <h3>{stats?.COMPLETED || 0}</h3>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="row mb-3">
          <div className="col-md-3">
            <select className="form-select" value={statusFilter} onChange={handleFilterChange(setStatusFilter)}>
              {statusOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-3">
            <select className="form-select" value={priorityFilter} onChange={handleFilterChange(setPriorityFilter)}>
              {priorityOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-3">
            <select className="form-select" value={sourceFilter} onChange={handleFilterChange(setSourceFilter)}>
              {sourceOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="row">
          <div className="col-sm-12">
            <div className="table-card-div">
              <div className="card-table">
                <div className="card-body purchase">
                  <div className="table-responsive table-hover">
                    <TableView
                      columns={columns}
                      scroll={tasks?.length > 0 ? { x: true } : false}
                      dataSource={tasks || []}
                      onRow={(record) => ({
                        onClick: () => handleRowClick(record),
                        style: { cursor: 'pointer' }
                      })}
                      paginationConfig={{
                        total: totalRecords || 0,
                        defaultPageSize: 10,
                        defaultCurrent: 1,
                        current: page,
                        pageSize: pagesize,
                        showSizeChanger: true,
                        pageSizeOptions: [10, 25, 50, 100],
                        onChange: (page, pageSize) => handlePagination(page, pageSize),
                        itemRender: itemRender
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      {showDetailModal && taskDetail && (
        <CustomModal isOpen={showDetailModal} toggle={toggleDetailModal} title="Host Task Detail" size="lg">
          <div className="row">
            <div className="col-md-6 mb-3">
              <strong>Title:</strong>
              <p>{taskDetail?.title || '-'}</p>
            </div>
            <div className="col-md-6 mb-3">
              <strong>Category:</strong>
              <p>{taskDetail?.category || '-'}</p>
            </div>
            <div className="col-md-12 mb-3">
              <strong>Description:</strong>
              <p>{taskDetail?.description || '-'}</p>
            </div>
            <div className="col-md-6 mb-3">
              <strong>Status:</strong>
              <p>{getStatusBadge(taskDetail?.status)}</p>
            </div>
            <div className="col-md-6 mb-3">
              <strong>Priority:</strong>
              <p>{getPriorityBadge(taskDetail?.priority)}</p>
            </div>
            <div className="col-md-6 mb-3">
              <strong>Source:</strong>
              <p>{taskDetail?.source?.replace('_', ' ') || '-'}</p>
            </div>
            <div className="col-md-6 mb-3">
              <strong>Host:</strong>
              <p>
                {taskDetail?.client
                  ? `${taskDetail.client.first_name || ''} ${taskDetail.client.last_name || ''}`.trim()
                  : '-'}
              </p>
            </div>
            <div className="col-md-6 mb-3">
              <strong>Property:</strong>
              <p>{taskDetail?.property?.property_name || '-'}</p>
            </div>
            <div className="col-md-6 mb-3">
              <strong>Created Date:</strong>
              <p>{taskDetail?.created_at ? convertTimestampToDate(taskDetail.created_at) : '-'}</p>
            </div>
            {taskDetail?.notes && (
              <div className="col-md-12 mb-3">
                <strong>Notes:</strong>
                <p>{taskDetail.notes}</p>
              </div>
            )}
          </div>
        </CustomModal>
      )}
    </div>
  )
}

export default HostTasks
